import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Image,
  ModalCloseButton,
  PinInput,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';

import AcceptApprovalImg from '../../../../../assets/images/accept-approval.svg';
import {
  AnimatedLogo,
  CustomModalActionPrimaryButton,
  CustomModalFooter,
  CustomPinInputField,
  PrimaryButton,
  StatusBox,
} from '../../../../../components/custom';
import SuccessButton from '../../../../../components/custom/CustomButtons/SuccessButton';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  approveTransactionAsync,
  resetTransactionApprove,
} from '../../../../../store/slices/transactionMandate/approveTransaction';
import { IPendingTransactionForApproval } from '../../../../../types/pendingTransaction';
import CreateTransactionPin from '../../../../TransactionPin/CreateTransactionPin';

interface ApproveTransactionMandateProps {
  transaction: IPendingTransactionForApproval;
}

type TApproveScreens = 'selection' | 'pin';

export default function ApproveTransactionMandate({
  transaction,
}: ApproveTransactionMandateProps) {
  const [approveScreen, setApproveScreen] =
    useState<TApproveScreens>('selection');

  const handleApprove = () => {
    setApproveScreen('pin');
  };

  const [pin, setPin] = useState('');

  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector((state) => state.approveTransaction);

  const handleSubmitApproveTransaction = () => {
    dispatch(
      approveTransactionAsync({
        pin,
        transactionId: transaction.id ?? '',
      })
    );
  };

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  if (!userHasPin) {
    return <CreateTransactionPin setUserHasPin={setUserHasPin} />;
  }

  if (approveScreen === 'pin') {
    if (status === 'loading') {
      return (
        <Box minH="330px">
          <AbsoluteCenter>
            <AnimatedLogo />
          </AbsoluteCenter>
        </Box>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox
                variant="success"
                title="Transaction Approved Successfully."
              >
                {value?.message ??
                  'The transaction has been successfully approved.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              as={ModalCloseButton}
              w="full"
              top="initial"
              right="initial"
            >
              Dismiss
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    if (status === 'failed') {
      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox variant="error" title="Failed to approve transaction.">
                {value?.message ??
                  'An error occurred while approving the transaction. Please try again.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              onClick={() => dispatch(resetTransactionApprove())}
              w="full"
            >
              Retry
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    return (
      <Box id={transaction.id}>
        <Box pt={10} pb={4}>
          <Text
            color="#1a1c1f"
            fontSize="1.5rem"
            lineHeight="133.33%"
            mb={3}
            textAlign="center"
          >
            PIN
          </Text>
          <Text
            fontSize="16px"
            color="gray.200"
            lineHeight="143.75%"
            textAlign="center"
            mb={3}
          >
            Enter your 4-digit PIN to reject this transaction
          </Text>
        </Box>

        <FormControl id="pin" mb="114px">
          <FormLabel fontSize=".875rem">Enter PIN</FormLabel>
          <HStack>
            <PinInput mask value={pin} onChange={(value) => setPin(value)}>
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
            </PinInput>
          </HStack>
        </FormControl>

        <CustomModalFooter>
          <CustomModalActionPrimaryButton
            isDisabled={!pin}
            onClick={handleSubmitApproveTransaction}
          >
            Save and Continue
          </CustomModalActionPrimaryButton>
        </CustomModalFooter>
      </Box>
    );
  }

  return (
    <Box id={transaction.id}>
      <Box pt={10} pb={4}>
        <Image
          src={AcceptApprovalImg}
          alt=""
          role="presentation"
          loading="lazy"
          boxSize={200}
          mx="auto"
          mb={4}
        />
        <Text fontSize="24px" lineHeight="133%" textAlign="center" mb={3}>
          Approve Transaction
        </Text>
        <Text color="gray.200" textAlign="center" letterSpacing="-0.12px">
          Are you sure you want to approve this transaction? This action is
          irreversible.
        </Text>
      </Box>
      <CustomModalFooter>
        <HStack spacing={4}>
          <SuccessButton w="full" onClick={handleApprove}>
            Yes, Approve
          </SuccessButton>
          <PrimaryButton
            w="full"
            as={ModalCloseButton}
            top="initial" // reset ModalCloseButton styles
            right="initial"
            flexShrink="initial"
          >
            Not Now
          </PrimaryButton>
        </HStack>
      </CustomModalFooter>
    </Box>
  );
}
